import React, { useState, useMemo } from 'react';
import {
  Card,
  Button,
  Dropdown,
  Table,
  Badge,
  Alert,
  Spinner,
  Modal,
  Form,
  InputGroup,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faFilePdf,
  faEnvelope,
  faTrash,
  faSpinner,
  faSms,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';

import { useUser } from '../../../contexts/UserContext';
import {
  useFetchUserDbqs,
  useCreateUserDbq,
  useDeleteUserDbq,
  useGenerateDbqPdf,
  useSendDbqEmail,
  useUpdateUserDbqStatus,
  DBQ_STATUSES,
} from '../../../services/userDbq';
import { useFetchDbqFormsList } from '../../../services/dbq';
import { isInternalUser } from '../../../lib/rbacConstants';

const DbqLegacyTab = ({ claimId, providers = [] }) => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedDbq, setSelectedDbq] = useState(null);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [includeComments, setIncludeComments] = useState(false);
  const [sendSms, setSendSms] = useState(false);
  const [dbqSearchTerm, setDbqSearchTerm] = useState('');

  const providerOptions = useMemo(() => {
    return providers.map((provider) => ({
      userId: provider.userId,
      email: provider.userEmail,
      phone: provider.userPhone || null,
      name:
        `${provider.userFirstName || ''} ${provider.userLastName || ''}`.trim() ||
        provider.userEmail,
    }));
  }, [providers]);

  const selectedProvider = useMemo(() => {
    return providerOptions.find((provider) => provider.userId === selectedProviderId);
  }, [providerOptions, selectedProviderId]);

  const isAdmin = isInternalUser(user?.userType);

  const { data: userDbqsData, isLoading: isLoadingUserDbqs } = useFetchUserDbqs(claimId);
  const userDbqs = userDbqsData?.userDbqs || [];

  const { data: formsData, isLoading: isLoadingForms } = useFetchDbqFormsList();
  const availableForms = useMemo(() => formsData?.forms || [], [formsData?.forms]);

  const filteredSortedForms = useMemo(() => {
    const sorted = [...availableForms].sort((a, b) =>
      (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }),
    );

    if (!dbqSearchTerm.trim()) {
      return sorted;
    }

    const searchLower = dbqSearchTerm.toLowerCase().trim();
    return sorted.filter((form) =>
      (form.title || '').toLowerCase().includes(searchLower) ||
      (form.shortName || '').toLowerCase().includes(searchLower),
    );
  }, [availableForms, dbqSearchTerm]);

  const createUserDbq = useCreateUserDbq(() => {
    toast.success('DBQ form created successfully!');
    queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
  });

  const deleteUserDbq = useDeleteUserDbq(() => {
    toast.success('DBQ form deleted successfully.');
    queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
  });

  const generatePdf = useGenerateDbqPdf(
    () => {
      toast.success('PDF generated successfully!');
      queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
      queryClient.invalidateQueries({ queryKey: ['claim-profile', claimId] });
    },
    (requestError) => {
      toast.error(requestError?.response?.data?.msg || 'Failed to generate PDF');
    },
  );

  const sendEmailMutation = useSendDbqEmail(
    () => {
      const message =
        sendSms && selectedProvider?.phone
          ? 'Email and text message sent successfully!'
          : 'Email sent successfully!';
      toast.success(message);
      setShowSendModal(false);
      setSelectedDbq(null);
      setSelectedProviderId('');
      setIncludeComments(false);
      setSendSms(false);
    },
    (requestError) => {
      toast.error(requestError?.response?.data?.msg || 'Failed to send email');
    },
  );

  const updateStatus = useUpdateUserDbqStatus(
    () => {
      toast.success('Status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['user-dbqs', claimId] });
    },
    (requestError) => {
      toast.error(requestError?.response?.data?.msg || 'Failed to update status');
    },
  );

  const handleStatusChange = (userDbqId, newStatus) => {
    updateStatus.mutate({ userDbqId, status: newStatus });
  };

  const handleCreateDbq = (dbqFormId) => {
    createUserDbq.mutate({ claimId, dbqFormId });
  };

  const handleEdit = (userDbq) => {
    window.open(`/dbq/edit/${userDbq.userDbqId}`, '_blank');
  };

  const handleGeneratePdf = (userDbqId) => {
    if (window.confirm('Generate PDF from this DBQ form? This will create a PDF file in your VA Forms folder.')) {
      generatePdf.mutate(userDbqId);
    }
  };

  const handleSend = (userDbq) => {
    setSelectedDbq(userDbq);
    setSelectedProviderId('');
    setIncludeComments(false);
    setSendSms(false);
    setShowSendModal(true);
  };

  const handleSendSubmit = () => {
    if (!selectedProvider) {
      toast.error('Please select a provider');
      return;
    }

    sendEmailMutation.mutate({
      userDbqId: selectedDbq.userDbqId,
      recipientEmail: selectedProvider.email,
      includeComments,
      sendSms: sendSms && !!selectedProvider.phone,
      recipientPhone: selectedProvider.phone,
    });
  };

  const handleDelete = (userDbq) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${userDbq.title}"?\n\n` +
      'This action cannot be undone. All form data will be permanently lost.',
    );

    if (!confirmed) return;

    const doubleConfirmed = window.confirm(
      'Please confirm: This will permanently delete this DBQ form and all its data.',
    );

    if (doubleConfirmed) {
      deleteUserDbq.mutate(userDbq.userDbqId);
    }
  };

  if (isLoadingUserDbqs || isLoadingForms) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-3 text-muted">Loading DBQ forms...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-1">DBQ Forms</h5>
              <p className="text-muted mb-0">Disability Benefits Questionnaires for this claim</p>
            </div>
            {availableForms.length > 0 ? (
              <Dropdown onToggle={(isOpen) => !isOpen && setDbqSearchTerm('')}>
                <Dropdown.Toggle variant="primary" disabled={createUserDbq.isPending}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Create DBQ
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ maxHeight: '400px', overflowY: 'auto', width: '280px' }}>
                  <div className="px-2 pb-2 sticky-top bg-white">
                    <InputGroup size="sm">
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faSearch} className="text-muted" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search..."
                        value={dbqSearchTerm}
                        onChange={(e) => setDbqSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </InputGroup>
                  </div>
                  {filteredSortedForms.length === 0 ? (
                    <Dropdown.ItemText className="text-muted text-center py-2">
                      No forms match "{dbqSearchTerm}"
                    </Dropdown.ItemText>
                  ) : (
                    filteredSortedForms.map((form) => (
                      <Dropdown.Item
                        key={form.dbqFormId}
                        onClick={() => handleCreateDbq(form.dbqFormId)}
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={form.title}
                      >
                        {form.title}
                      </Dropdown.Item>
                    ))
                  )}
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <Alert variant="warning" className="mb-0">
                No DBQ forms with templates configured yet. Configure templates in the DBQ Library.
              </Alert>
            )}
          </div>

          {userDbqs.length === 0 ? (
            <Alert variant="info">
              <p className="mb-0">
                No DBQ forms yet. Click "Create DBQ" to get started.
              </p>
            </Alert>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Form Title</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {userDbqs.map((userDbq) => (
                  <tr key={userDbq.userDbqId}>
                    <td>
                      <strong>{userDbq.title}</strong>
                      {userDbq.shortName && (
                        <div className="text-muted small">{userDbq.shortName}</div>
                      )}
                      {userDbq.version && (
                        <Badge bg="secondary" className="ms-2">{userDbq.version}</Badge>
                      )}
                    </td>
                    <td>
                      <div>{moment(userDbq.createdAt).format('MMM D, YYYY')}</div>
                      <div className="text-muted small">
                        {moment(userDbq.createdAt).format('h:mm A')}
                      </div>
                    </td>
                    <td>
                      <div>{moment(userDbq.updatedAt).format('MMM D, YYYY')}</div>
                      <div className="text-muted small">
                        {moment(userDbq.updatedAt).format('h:mm A')}
                      </div>
                    </td>
                    <td>
                      {isAdmin ? (
                        <Dropdown
                          onSelect={(nextStatus) => handleStatusChange(userDbq.userDbqId, nextStatus)}
                        >
                          <Dropdown.Toggle
                            variant={DBQ_STATUSES[userDbq.status || 'draft']?.variant || 'secondary'}
                            size="sm"
                            disabled={updateStatus.isPending}
                          >
                            {DBQ_STATUSES[userDbq.status || 'draft']?.label || 'Draft'}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {Object.entries(DBQ_STATUSES).map(([key, { label }]) => (
                              <Dropdown.Item
                                key={key}
                                eventKey={key}
                                active={userDbq.status === key || (!userDbq.status && key === 'draft')}
                              >
                                {label}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      ) : (
                        <Badge bg={DBQ_STATUSES[userDbq.status || 'draft']?.variant || 'secondary'}>
                          {DBQ_STATUSES[userDbq.status || 'draft']?.label || 'Draft'}
                        </Badge>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2 justify-content-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(userDbq)}
                          title="Edit form"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleGeneratePdf(userDbq.userDbqId)}
                          disabled={generatePdf.isPending}
                          title="Save as PDF"
                        >
                          {generatePdf.isPending ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <FontAwesomeIcon icon={faFilePdf} />
                          )}
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleSend(userDbq)}
                          disabled={sendEmailMutation.isPending}
                          title="Send via email"
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(userDbq)}
                          disabled={deleteUserDbq.isPending}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showSendModal} onHide={() => setShowSendModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Send DBQ to Provider</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select Provider</Form.Label>
            {providerOptions.length === 0 ? (
              <Alert variant="warning" className="mb-0">
                No providers are assigned to this claim. Please assign a provider first.
              </Alert>
            ) : (
              <Form.Select
                value={selectedProviderId}
                onChange={(e) => setSelectedProviderId(e.target.value)}
              >
                <option value="">-- Select a provider --</option>
                {providerOptions.map((provider) => (
                  <option key={provider.userId} value={provider.userId}>
                    {provider.name} ({provider.email})
                  </option>
                ))}
              </Form.Select>
            )}
            <Form.Text className="text-muted">
              The provider will receive an email with a link to complete the form.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="include-comments-checkbox"
              label="Include comments/notes in email"
              checked={includeComments}
              onChange={(e) => setIncludeComments(e.target.checked)}
            />
            <Form.Text className="text-muted">
              If checked, any unresolved comments on the form will be included in the email body.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Check
              type="checkbox"
              id="send-sms-checkbox"
              label={
                <>
                  <FontAwesomeIcon icon={faSms} className="me-1" />
                  Also send a text message
                </>
              }
              checked={sendSms}
              onChange={(e) => setSendSms(e.target.checked)}
              disabled={!selectedProvider?.phone}
            />
            <Form.Text className="text-muted">
              {selectedProvider?.phone
                ? 'The provider will also receive an SMS notification with the form link.'
                : selectedProviderId
                  ? 'This provider does not have a phone number on file.'
                  : 'Select a provider to enable SMS notification.'}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSendModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendSubmit}
            disabled={sendEmailMutation.isPending || providerOptions.length === 0 || !selectedProviderId}
          >
            {sendEmailMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Sending...
              </>
            ) : (
              'Send to Provider'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DbqLegacyTab;
